import { useCallback, useEffect, useState } from 'react'

import { DocumentReference, FirestoreDataConverter, Timestamp } from 'firebase/firestore'

import { deleteDocument, setDocument, setDocumentByRef } from '../DocumentsDAO'
import { debounce } from '../debounce'
import { GetCollectionParams, getCollection, listenCollection } from '../getCollection'
import { FSDocument } from '../types'

type EntityUpdate<T> = Omit<T, 'id' | keyof FSDocument> & { id: string }

interface UseCollectionsResponse<T extends FSDocument> {
    results: T[]
    hasReachedEnd: boolean
    loadMore: () => void
    isLoading: boolean
    addDocument: (data: EntityUpdate<T>) => Promise<T>
    updateDocument: (id: string, data: EntityUpdate<T>) => Promise<void>
    updateDocumentSync: (id: string, data: EntityUpdate<T>) => Promise<void>
    removeDocument: (id: string) => Promise<void>
    removeDocumentFromState: (id: string) => void
}

const removeDuplicates = <T extends FSDocument>(a: T[], b: T[]): T[] => {
    const uniques = new Map()

    a.forEach(value => uniques.set(value.id, value))
    b.forEach(value => uniques.set(value.id, value))

    return Array.from(uniques.values())
}

const baseConverter: FirestoreDataConverter<any, any> = {
    toFirestore: (doc) => doc,
    fromFirestore: (snapshot, options) => {
        const data = snapshot.data(options)
        return {
            ...data,
            id: snapshot.id,
        } as any
    },
}

export const useCollection = <T extends FSDocument>({
    path,
    orderBy,
    limit,
    where,
    converter = baseConverter,
    isCollectionGroup,
}: GetCollectionParams): UseCollectionsResponse<T> => {
    const params = { path, orderBy, limit, where, converter }
    const [startAfter, setStartAfter] = useState<number | undefined>(undefined)
    const [hasReachedEnd, setHasReachedEnd] = useState(false)
    const [results, setResults] = useState<T[]>([])
    const [docRefs, setDocRefs] = useState<DocumentReference[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const setDocumentDebounced = useCallback(debounce(setDocumentByRef, 1000), [
        path,
        limit,
        startAfter,
        where,
        converter,
    ])

    // Reset pagination when where clauses change
    useEffect(() => {
        setStartAfter(undefined)
        setHasReachedEnd(false)
    }, [where])

    useEffect(() => {
        if (!results.length) setIsLoading(true)
        let unsubscribe = () => {}
        const fetchResults = async () => {
            // if where clause is an in condition with an empty array then return empty to avoid firebase error
            if (where && where[0] && where[0][1] === 'in' && (where[0][2] as string[]).length === 0) {
                setResults([])
                setIsLoading(false)
                setHasReachedEnd(true)
                return
            }

            const docs = await getCollection<T>({ ...params, where, startAfter, converter, isCollectionGroup })
            if (startAfter) {
                // This is used just for pagination
                setResults(previousDocs =>
                    removeDuplicates(
                        previousDocs,
                        docs.map(doc => doc.data()),
                    ),
                )
                setDocRefs(previousDocs =>
                    removeDuplicates(
                        previousDocs,
                        docs.map(doc => doc.ref),
                    ),
                )
            } else {
                setResults(docs.map(doc => doc.data()))
                setDocRefs(docs.map(doc => doc.ref))
            }

            setIsLoading(false)
            unsubscribe = await listenCollection<T>(
                // TODO: LISTEN TO DOCUMENT DELETIONS:
                // When a document is deleted, the snapshot’s docChanges()
                // will include a change with type set to "removed"
                { ...params, where, startAfter, converter, isCollectionGroup },
                upToDateDocs => {
                    if (upToDateDocs.length) {
                        setResults(prev => {
                            return startAfter // This is used just for pagination
                                ? removeDuplicates(
                                      prev,
                                      upToDateDocs.map(doc => doc.data()),
                                  )
                                : upToDateDocs.map(doc => doc.data())
                        })

                        setDocRefs(previousDocs =>
                            removeDuplicates(
                                previousDocs,
                                upToDateDocs.map(doc => doc.ref),
                            ),
                        )
                    } else if (!startAfter) {
                        setResults([])
                        setDocRefs([])
                    }

                    if (!limit || upToDateDocs.length < limit) {
                        setHasReachedEnd(true)
                    }
                },
            )
        }

        fetchResults()
        return () => {
            unsubscribe() // TODO during pagination, when loadMore is called, the previous docs are not being listened any more
        }
    }, [path, limit, startAfter, where, converter])

    const loadMore = () => {
        if (!results.length) setHasReachedEnd(true)
        else {
            const orderByField = orderBy![0] as string
            const lastElementTime = results[results.length - 1][orderByField] as number
            setStartAfter(lastElementTime)
        }
    }

    const addDocument = useCallback(
        async (data: EntityUpdate<T>) => {
            if (isCollectionGroup) {
                throw new Error('addDocument is not supported for collection groups')
            }
            const timestamp = Timestamp.now()
            const docWithTimestamps = {
                ...(data as object),
                createdAt: timestamp,
                updatedAt: timestamp,
            }
            const newDoc = await setDocument<typeof docWithTimestamps>({
                collectionName: path,
                data: docWithTimestamps,
                converter,
            })
            const typedDoc = newDoc as unknown as T
            setResults(prev => [...prev, typedDoc])
            return typedDoc
        },
        [path, converter],
    )

    const updateDocument = useCallback(
        async (id: string, data: EntityUpdate<T>) => {
            try {
                const docRef = docRefs.find(doc => doc.id === id)
                if (!docRef) {
                    throw new Error(`Document not found: ${id}`)
                }
                setDocumentDebounced(docRef, data, converter)
                const updatedData = {
                    ...(data as object),
                    updatedAt: Timestamp.now(),
                }
                setResults(prev => prev?.map(doc => (doc.id === id ? ({ ...doc, ...updatedData } as T) : doc)) ?? [])
            } catch (error) {
                console.error('updateDocument - Error al actualizar:', error)
                Sentry.captureException(error)
            }
        },
        [path, converter, docRefs, results, setDocumentDebounced],
    )

    const updateDocumentSync = useCallback(
        async (id: string, data: EntityUpdate<T>) => {
            try {
                const docRef = docRefs.find(doc => doc.id === id)
                if (!docRef) {
                    throw new Error(`Document not found: ${id}`)
                }
                await setDocumentByRef(docRef, data, converter)
                const updatedData = {
                    ...(data as object),
                    updatedAt: Timestamp.now(),
                }
                setResults(prev => prev?.map(doc => (doc.id === id ? ({ ...doc, ...updatedData } as T) : doc)) ?? [])
            } catch (error) {
                console.error('updateDocumentSync - Error al actualizar:', error)
                Sentry.captureException(error)
            }
        },
        [path, converter, docRefs, results],
    )

    const removeDocument = useCallback(
        async (id: string) => {
            setResults(prev => prev?.filter(doc => doc.id !== id) ?? [])
            await deleteDocument({
                collectionName: path,
                id,
                converter,
            })
        },
        [path, converter],
    )

    const removeDocumentFromState = (id: string) => {
        setResults(prev => prev?.filter(doc => doc.id !== id) ?? [])
    }

    return {
        results: results ?? [],
        hasReachedEnd,
        loadMore,
        isLoading,
        addDocument,
        updateDocument,
        updateDocumentSync,
        removeDocument,
        removeDocumentFromState,
    }
}
